/**
 * Geometric helpers for GeoJSON features.
 * Pure functions — no React, no map instance, no module-level state.
 */

import { stateFipsToName, normalizeAreaIdForLevel, normalizeId } from "../idUtils.js";

/**
 * Returns the naive centroid [lng, lat] of a GeoJSON Polygon or MultiPolygon
 * feature by averaging all vertices.  Returns null for any other geometry type,
 * a missing/null geometry, or a feature with no finite coordinate pairs.
 *
 * @param {object|null} geoJsonFeature  A GeoJSON Feature object.
 * @returns {[number, number]|null}
 */
export function getFeatureCentroid(geoJsonFeature) {
    try {
      const geom = geoJsonFeature?.geometry;
      if (!geom) return null;
      const coords = geom.type === "Polygon"
        ? geom.coordinates.flat(2)
        : geom.type === "MultiPolygon"
          ? geom.coordinates.flat(3)
          : [];
      if (!coords.length) return null;
      let x = 0;
      let y = 0;
      let n = 0;
      for (let i = 0; i < coords.length; i += 2) {
        const lon = coords[i];
        const lat = coords[i + 1];
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
        x += lon;
        y += lat;
        n += 1;
      }
      if (!n) return null;
      return [x / n, y / n];
    } catch {
      return null;
    }
}

/**
 * Returns a human-readable display label for a GeoJSON feature given its geo
 * level.  The second argument is the feature's .properties bag (shapefile
 * attributes — NOT React component props).
 *
 * @param {string} level        Geo level: "cd", "statesenate", "statehouse",
 *                              "counties", "zipcodes", or any other (→ default).
 * @param {object} featureProps Feature .properties bag from the parsed FGB.
 * @returns {string}            Display label; never null/undefined, may be "".
 */
export function getFeatureDisplayName(level, featureProps = {}) {
    const stateName = stateFipsToName[String(featureProps.STATEFP || "").padStart(2, "0")] || featureProps.state || "";
    if (level === "cd") {
      const cd = String(featureProps.CD119FP ?? featureProps.CD120FP ?? featureProps.CD118FP ?? featureProps.CD116FP ?? featureProps.NAME ?? "").padStart(2, "0");
      return stateName && cd ? `${stateName} - ${cd}` : (featureProps.NAME || featureProps.name || featureProps.area_id || "");
    }
    if (level === "statesenate") {
      const seat = String(featureProps.SLDUST ?? featureProps.SLD_Upper ?? featureProps.NAME ?? featureProps.area_id ?? "");
      return stateName && seat ? `${stateName} - ${seat}` : seat;
    }
    if (level === "statehouse") {
      const seat = String(featureProps.SLDLST ?? featureProps.SLD_Lower ?? featureProps.NAME ?? featureProps.area_id ?? "");
      return stateName && seat ? `${stateName} - ${seat}` : seat;
    }
    if (level === "counties") {
      return featureProps.NAME && stateName ? `${featureProps.NAME}, ${stateName}` : (featureProps.NAME || featureProps.name || featureProps.area_id || "");
    }
    if (level === "zipcodes") {
      return featureProps.GEOID20 && featureProps.NAME ? `${featureProps.GEOID20} - ${featureProps.NAME}` : (featureProps.NAME || featureProps.name || featureProps.area_id || "");
    }
    return featureProps.NAME || featureProps.name || featureProps.area_id || "";
}

// Re-exported so MapPage and other consumers can import normalizeAreaIdForLevel from
// this module unchanged.  Canonical implementation lives in idUtils.js.
export { normalizeAreaIdForLevel };

/**
 * Searches a GeoJSON features array for the feature whose ID matches areaId
 * at the given geo level, trying multiple candidate properties per level.
 * Returns the first matching feature or null.
 *
 * Verbatim lift of the MapPage class method. Uses normalizeAreaIdForLevel
 * from this module (not this.normalize… / not idUtils).
 *
 * @param {string}  level    Geo level string.
 * @param {*}       areaId   ID to match.
 * @param {Array}   features Array of GeoJSON features (with .properties).
 * @returns {object|null}    The matched feature or null.
 */
export function findFeatureForAreaId(level, areaId, features = []) {
    const target = normalizeAreaIdForLevel(level, areaId);
    const rawTarget = String(areaId ?? "");
    const getCandidatesForLevel = (props = {}) => {
      switch (level) {
        case "states":
          return [props.STATEFP, props.GEOID, props.area_id];
        case "cd": {
          const statefp = String(props.STATEFP || "").padStart(2, "0");
          const district = String(props.CD119FP ?? props.CD120FP ?? props.CD118FP ?? props.CD116FP ?? "").padStart(2, "0");
          return [
            props.area_id,
            props.GEOID,
            statefp && district ? `${statefp}${district}` : null,
            district,
          ];
        }
        case "counties": {
          const statefp = String(props.STATEFP || "").padStart(2, "0");
          const countyfp = String(props.COUNTYFP || "").padStart(3, "0");
          return [
            props.area_id,
            props.GEOID,
            statefp && countyfp ? `${statefp}${countyfp}` : null,
          ];
        }
        case "zipcodes":
          return [props.area_id, props.GEOID20, props.ZCTA5CE20];
        case "statesenate":
          return [props.area_id, props.GEOID, props.SLD_Upper, props.SLDUST];
        case "statehouse":
          return [props.area_id, props.GEOID, props.SLD_Lower, props.SLDLST];
        case "media_market":
          return [props.area_id, props.GEOID];
        default:
          return [props.area_id, props.GEOID];
      }
    };
    return features.find((f) => {
      const props = f?.properties || {};
      const candidates = getCandidatesForLevel(props)
        .filter((v) => v != null)
        .map((v) => String(v));

      return candidates.some((candidate) => {
        const normalized = normalizeAreaIdForLevel(level, candidate);
        return (
          normalized === target ||
          candidate === rawTarget
        );
      });
    }) || null;
}

/**
 * Returns a Set of normalised area IDs that fall in the top-N% by score,
 * or null when the inputs are empty / concentration is "all" / no valid rows.
 *
 * Verbatim lift of the MapPage class method with ONE seam change:
 * the implicit `this.props.geoLevel` read is made explicit via the `geoLevel`
 * parameter — the class delegator passes `this.props.geoLevel`.
 *
 * @param {Map}    byId          Area-id keyed metric rows.
 * @param {Array}  features      GeoJSON features with .properties.area_id.
 * @param {object} group         Config object; TargetVariable1.polarity used when subVar==="net".
 * @param {string} subVar        Column name or "net".
 * @param {string} concentration "top50" | "top25" | "top10" | "all" | other → null.
 * @param {string} geoLevel      Geo level string (was this.props.geoLevel in class).
 * @returns {Set<string>|null}
 */
export function getConcentrationSet(byId, features, group, subVar, concentration, geoLevel) {
    const normalizeAreaId = (id) => normalizeAreaIdForLevel(geoLevel, id);
    if (!features || !features.length) return null;
    if (!concentration || concentration === "all") return null;

    const pctMap = { top50: 50, top25: 25, top10: 10 };
    const pct = pctMap[concentration];
    if (!pct) return null;

    // API values are already percentages (0..100), including sub-1% values.
    const toPercent = (x) => (!Number.isFinite(x) ? null : x);
    const toArray = (v) => (Array.isArray(v) ? v : v != null ? [v] : []);
    const sumKeys = (keys, row) =>
      keys.reduce((acc, k) => acc + (Number(row?.[k]) || 0), 0);

    const values = features
      .map((f) => {
        const id = normalizeAreaId(f?.properties?.area_id ?? "");
        const row = byId?.get(id);
        if (!row) return null;
        let val;
        if (subVar === "net") {
          const posKeys = toArray(group?.TargetVariable1?.polarity?.pos);
          const negKeys = toArray(group?.TargetVariable1?.polarity?.neg);
          const pos = sumKeys(posKeys, row);
          const neg = sumKeys(negKeys, row);
          val = Number.isFinite(pos) && Number.isFinite(neg) ? pos - neg : null;
        } else {
          val = Number(row?.[subVar]);
        }
        const pctVal = toPercent(val);
        return Number.isFinite(pctVal) ? { id, v: pctVal } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.v - a.v);

    if (!values.length) return null;
    const keepCount = Math.max(1, Math.ceil((pct / 100) * values.length));
    const keep = new Set(values.slice(0, keepCount).map((d) => d.id));
    return keep;
}

/**
 * Pure core of loadGeo (P3–P7): transforms deserialized FGB features into a
 * normalised GeoJSON FeatureCollection and a sorted PossibleAreas search index.
 *
 * Verbatim lift of the synchronous window between the _geoLoadIds guard check
 * (position #9 in the async skeleton) and the handlePossibleAreasChange callback
 * (position #10). Every await, guard check, setState, and fitBounds remains in
 * the loadGeo wrapper exactly where it was.
 *
 * ⚠  Punch-list (preserved verbatim, do not fix):
 *   - Zipcodes PossibleAreas sort reads `a.GEOID20` but the mapped objects have
 *     field `GEOID` — NaN comparator → stable insertion order (FGB file order).
 *
 * @param {Array}        features   Raw GeoJSON features from fgb.deserialize.
 * @param {string}       level      Geo level (already overridden by ALLOWED_LEVELS).
 * @param {string|null}  stateCode  State FIPS or null for national.
 * @returns {{ geo: GeoJSONFeatureCollection, possibleAreas: Array }}
 */
export function processGeoFeatures(features, level, stateCode) {
      // --- detect field used for state filtering ---
      const stateField =
        features.length && "STATE" in features[0].properties
          ? "STATE"
          : "STATEFP";

      // --- filter by state if applicable ---
      let filteredFeatures = features;
      if (stateCode && level !== "states" && level !== "zipcodes" && level !== "media_market") {
        const padded = String(stateCode).padStart(2, "0");
        filteredFeatures = features.filter(f => String(f.properties?.[stateField]) === padded);
      }

      if (level === "cd") {
        filteredFeatures = filteredFeatures.map(f => {
          const props = { ...f.properties };
          props.NAME = stateFipsToName[props.STATEFP] + " - " + (props.CD119FP ?? props.CD120FP ?? "");
          return { ...f, properties: props };
        });
      }

      const geo = {
        type: "FeatureCollection",
        features: filteredFeatures.map((f, index) => {
          let areaId = normalizeId(level, f.properties);
          if (level === "statehouse" && areaId.startsWith("50")) {
            areaId = areaId.replace(/-/g, "");
          }
          return {
            ...f,
            id: index,
            properties: {
              ...f.properties,
              area_id: areaId,
            },
          };
        }),
      };

      let PossibleAreas;
      switch (level) {
        case "counties":
          PossibleAreas = geo.features.map(f => {
            const p = f.properties;
            const stateName = stateFipsToName[p.STATEFP] || "";
            return {
              name: `${p.NAME}, ${stateName}`,
              countyName: p.NAME,
              stateName,
              GEOID: p.GEOID,
              area_id: p.area_id
            };
          })
          .sort((a, b) => {
            const stateCompare = a.stateName.localeCompare(b.stateName);
            if (stateCompare !== 0) return stateCompare;
            return a.countyName.localeCompare(b.countyName);
          });
          break;

        case "zipcodes":
          PossibleAreas = geo.features.map(f => ({
            name: f.properties.area_id + " - " + f.properties.NAME,
            GEOID: f.properties.GEOID20
          // ⚠ SORT BUG (verbatim): reads a.GEOID20 but objects have field GEOID;
          // NaN comparator → stable sort → FGB insertion order preserved.
          })).sort((a, b) => Number(a.GEOID20) - Number(b.GEOID20));
          break;

        case "statesenate":
        case "statehouse":
          PossibleAreas = geo.features.map(f => {
            let geoId = String(f.properties.area_id);
            return {
              name: f.properties.NAME,
              GEOID: geoId,
            };
          }).sort((a, b) => Number(a.GEOID) - Number(b.GEOID));
          break;

        case "media_market":
          PossibleAreas = geo.features.map(f => ({
            name: f.properties.NAME,
            GEOID: f.properties.Key,
            area_id: f.properties.area_id,
          })).sort((a, b) => a.name.localeCompare(b.name));
          break;

        default:
          PossibleAreas = geo.features.map(f => ({
            name: f.properties.NAME,
            GEOID: f.properties.GEOID
          })).sort((a, b) => Number(a.GEOID) - Number(b.GEOID));
          break;
      }

      return { geo, possibleAreas: PossibleAreas };
}
